import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Phone,
  Email,
  Person,
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';

const RecentLeads = ({ leads = [] }) => {
  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      'new': 'primary',
      'contacted': 'warning',
      'qualified': 'success',
      'proposal': 'secondary',
      'negotiation': 'info',
      'closed_won': 'success',
      'closed_lost': 'error',
      'follow_up': 'default',
      'warm': 'warning',
      'hot': 'error',
      'cold': 'info',
    };
    
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
    return statusColors[normalizedStatus] || 'default';
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Invalid Date';
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Handle empty state
  if (!leads || leads.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Leads
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 300,
              color: 'text.secondary',
            }}
          >
            <Person sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1">
              No recent leads found
            </Typography>
            <Typography variant="caption" sx={{ mt: 1 }}>
              New leads will appear here once they're added to the system
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', minHeight: 400 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Recent Leads
        </Typography>
        <TableContainer sx={{ maxHeight: 350 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.slice(0, 10).map((lead, index) => (
                <TableRow 
                  key={lead.id || index}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 2, 
                          bgcolor: 'primary.main',
                          fontSize: '0.875rem'
                        }}
                      >
                        {getInitials(lead.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {lead.name || 'Unknown'}
                        </Typography>
                        {lead.email && (
                          <Typography variant="caption" color="text.secondary">
                            {lead.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.status || 'Unknown'}
                      size="small"
                      color={getStatusColor(lead.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {lead.company || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(lead.created_at || lead.date_created)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary">
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {lead.phone && (
                        <Tooltip title="Call Lead">
                          <IconButton size="small" color="success">
                            <Phone fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {lead.email && (
                        <Tooltip title="Send Email">
                          <IconButton size="small" color="info">
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {leads.length > 10 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Showing 10 of {leads.length} recent leads
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentLeads;